import { Args, AxiosConfig, AxiosPagePromise } from './BaseService';
import { Page } from '../models/common/Page';
import { AxiosPromise } from 'axios';
import { resources } from '../resources';
import { ActivityRequestDto } from '../models/request/invitation/ActivityRequestDto';
import { UpdateInvitationDocument } from '../models/invitation/UpdateInvitationDocument';
import { MultipleIdRequest } from '../models/common/MultipleIdRequest';
import { CrudService } from './CrudService';
import { InvitationQueryParams } from '../params/InvitationQueryParams';
import { InvitationDto } from '../models/invitation/InvitationDto';
import { CreateUpdateInvitation } from '../models/invitation/CreateUpdateInvitation';
import { UnarchiveInvitationRequest } from '../models/invitation/UnarchiveInvitationRequest';
import { ConfirmInvitationRequest } from '../models/invitation/ConfirmInvitationRequest';
import { AddAttachmentRequest } from '../models/invitation/AddAttachmentRequest';
import { FileUploadMediaResponse } from '../models/invitation/FileUploadMediaResponse';
import { ApproveRiskRequest } from '../models/risk/ApproveRiskRequest';
import { SetActivityDocuments } from '../models/invitation/SetActivityDocuments';
import { InvitationStateAud } from '../models/invitation/InvitationStateAud';
import { InvitationLogEntryResponse } from '../models/invitation/InvitationLogEntryResponse';
import { InvitationLogEntryQueryParams } from '../params/InvitationLogEntryQueryParams';
import { ReferenceDto } from '../models/employees/ReferenceDto';
import { InvitationDocsExportType } from '../models/enums/InvitationDocsExportType';

export class InvitationService extends CrudService<InvitationDto, InvitationQueryParams, CreateUpdateInvitation> {
  constructor() {
    super({
      get: resources.getInvitationById,
      query: resources.queryInvitations,
      create: resources.createInvitation,
      update: resources.updateInvitation,
      delete: resources.deleteInvitation,
      idKey: 'invitationId',
    });
  }
  public getInvitationAudit(invitationId: string, args?: Args, options?: AxiosConfig): AxiosPagePromise<InvitationStateAud> {
    return this.get<Page<InvitationStateAud>>(resources.getInvitationAudit, { invitationId, ...args }, options);
  }
  public approveRisks(invitationId: string, data: ApproveRiskRequest): AxiosPromise<void> {
    return this.post<void>(resources.approveRisks, data, { invitationId });
  }
  public approveActivities(invitationId: string, data: MultipleIdRequest): AxiosPromise<void> {
    return this.post<void>(resources.approveActivities, data, { invitationId });
  }
  public requestActivity(data: ActivityRequestDto, options?: Args): AxiosPromise<void> {
    return this.post<void>(resources.requestActivity, data, options);
  }
  public addDocumentsForActivities(invitationId: string, data: SetActivityDocuments): AxiosPromise<void> {
    return this.put<void>(resources.addDocumentsForActivities, data, { invitationId });
  }
  public generateZipFile(invitationId: string, entries?: string[], exportType?: InvitationDocsExportType): AxiosPromise<Blob> {
    return this.post<Blob>(resources.generateZipFile, { entries }, { invitationId, exportType }, { responseType: 'blob' });
  }
  public getInvitationHistory(
    invitationId: string,
    args?: InvitationLogEntryQueryParams,
    options?: AxiosConfig,
  ): AxiosPagePromise<InvitationLogEntryResponse> {
    return this.get<Page<InvitationLogEntryResponse>>(resources.getInvitationHistory, { invitationId, ...args }, options);
  }
  public getInvitationHistoryPersons(invitationId: string, options?: AxiosConfig): AxiosPromise<ReferenceDto[]> {
    return this.get<ReferenceDto[]>(resources.getInvitationHistoryPersons, { invitationId }, options);
  }
  public updateDocument(invitationId: string, certificateId: string, data: UpdateInvitationDocument): AxiosPromise<void> {
    return this.put<void>(resources.updateDocument, data, { invitationId, certificateId });
  }
  public approveDocumentCertificate(invitationId: string, data: MultipleIdRequest): AxiosPromise<void> {
    return this.put<void>(resources.approveDocumentCertificate, data, { invitationId });
  }
  public unarchive(invitationId: string, data?: UnarchiveInvitationRequest): AxiosPromise<void> {
    return this.put<void>(resources.unarchiveInvitation, data, { invitationId });
  }
  public confirmInvitationDetails(invitationId: string, data: ConfirmInvitationRequest): AxiosPromise<void> {
    return this.put<void>(resources.confirmInvitationDetails, data, { invitationId });
  }
  public sendNotification(invitationId: string): AxiosPromise<void> {
    return this.put<void>(resources.sendInvitationNotification, undefined, { invitationId });
  }
  public addAttachment(invitationId: string, data: AddAttachmentRequest): AxiosPromise<FileUploadMediaResponse[]> {
    return this.post<FileUploadMediaResponse[]>(resources.addAttachment, data, { invitationId });
  }
  public deleteAttachments(invitationId: string, data: MultipleIdRequest): AxiosPromise<void> {
    return this.delete<void>(resources.deleteAttachments, { invitationId }, { data });
  }
}
